import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, InputNumber, Spin, Table, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getCouvertureSinistre } from '../variables/api/imputationService';
import { formatMontant } from '../variables/constants';

const { Text } = Typography;

/**
 * Sélecteur d'imputation : permet à l'utilisateur d'allouer manuellement
 * un montant à chaque encaissement actif du sinistre, en validant que
 * Σ imputations == montantCible.
 *
 * Props:
 *  - sinistreTrackingId : UUID du sinistre (requis pour fetch)
 *  - montantCible : montant à imputer au total (number)
 *  - prefiSuggere : { trackingId, libelle, resteRembourser } optionnel
 *      → pré-remplissage automatique sur le 1er encaissement à concurrence du préfi
 *  - onChange : callback({ imputations, totalImpute, isComplete })
 *      → imputations = [{ encaissementTrackingId, montant }]
 *      → totalImpute = somme des montants
 *      → isComplete = totalImpute === montantCible
 *  - disabled : bool
 */
const EncaissementImputationPicker = ({
    sinistreTrackingId,
    montantCible,
    prefiSuggere,
    onChange,
    disabled = false,
}) => {
    const { t } = useTranslation('commun');
    const [imputations, setImputations] = useState({}); // { encaissementTrackingId: montant }
    const [hasAutoFilled, setHasAutoFilled] = useState(false);

    const { data: couverture, isLoading, isError } = useQuery({
        queryKey: ['couverture-sinistre', sinistreTrackingId],
        queryFn: () => getCouvertureSinistre(sinistreTrackingId),
        enabled: !!sinistreTrackingId,
    });

    const encaissements = useMemo(() => couverture?.encaissements || [], [couverture]);

    // Pré-remplissage automatique pour préfi suggéré (Q10)
    useEffect(() => {
        if (
            prefiSuggere &&
            encaissements.length > 0 &&
            !hasAutoFilled &&
            Number(prefiSuggere.resteRembourser) > 0
        ) {
            const enc = encaissements[0];
            const montantAuto = Math.min(
                Number(enc.resteDisponible) || 0,
                Number(prefiSuggere.resteRembourser) || 0,
            );
            if (montantAuto > 0) {
                setImputations({ [enc.encaissementTrackingId]: montantAuto });
                setHasAutoFilled(true);
            }
        }
    }, [prefiSuggere, encaissements, hasAutoFilled]);

    const totalImpute = useMemo(
        () => Object.values(imputations).reduce((acc, v) => acc + (Number(v) || 0), 0),
        [imputations],
    );

    const isComplete = Math.abs(totalImpute - Number(montantCible || 0)) < 0.005;

    // Propage l'état au parent à chaque changement
    useEffect(() => {
        const flat = Object.entries(imputations)
            .filter(([, montant]) => Number(montant) > 0)
            .map(([encaissementTrackingId, montant]) => ({
                encaissementTrackingId,
                montant: Number(montant),
            }));
        if (typeof onChange === 'function') {
            onChange({ imputations: flat, totalImpute, isComplete });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [imputations, totalImpute, isComplete]);

    const handleChange = (encaissementTrackingId, max) => (val) => {
        const clamped = Math.min(Math.max(0, Number(val || 0)), Number(max || 0));
        setImputations((prev) => ({ ...prev, [encaissementTrackingId]: clamped }));
    };

    const columns = [
        {
            title: t('encaissementImputationPicker.colonnes.numeroCheque'),
            dataIndex: 'numeroCheque',
            width: 130,
            render: (v) => v || '-',
        },
        {
            title: t('encaissementImputationPicker.colonnes.montant'),
            dataIndex: 'montantCheque',
            width: 140,
            align: 'right',
            render: (v) => formatMontant(v),
        },
        {
            title: t('encaissementImputationPicker.colonnes.resteDispo'),
            dataIndex: 'resteDisponible',
            width: 140,
            align: 'right',
            render: (v) => (
                <Text strong style={{ color: Number(v) > 0 ? '#1a6b3c' : '#9ca3af' }}>
                    {formatMontant(v)}
                </Text>
            ),
        },
        {
            title: t('encaissementImputationPicker.colonnes.imputation'),
            width: 160,
            align: 'right',
            render: (_, row) => (
                <InputNumber
                    min={0}
                    max={Number(row.resteDisponible) || 0}
                    step={1000}
                    value={imputations[row.encaissementTrackingId] || 0}
                    onChange={handleChange(row.encaissementTrackingId, row.resteDisponible)}
                    disabled={disabled || Number(row.resteDisponible) <= 0}
                    style={{ width: '100%' }}
                    formatter={(v) =>
                        `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
                    }
                    parser={(v) => `${v}`.replace(/ /g, '').replace(/\s/g, '')}
                />
            ),
        },
    ];

    if (!sinistreTrackingId) {
        return <Alert type="info" message={t('encaissementImputationPicker.selectionnezSinistre')} />;
    }

    if (isLoading) {
        return (
            <div style={{ padding: 24, textAlign: 'center' }}>
                <Spin tip={t('encaissementImputationPicker.chargementCouverture')} />
            </div>
        );
    }

    if (isError) {
        return <Alert type="error" message={t('encaissementImputationPicker.chargementImpossible')} />;
    }

    if (encaissements.length === 0) {
        return (
            <Alert
                type="warning"
                showIcon
                message={t('encaissementImputationPicker.aucunCheque')}
            />
        );
    }

    return (
        <div>
            {prefiSuggere && Number(prefiSuggere.resteRembourser) > 0 && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message={t('encaissementImputationPicker.prefiActif', {
                        libelle: prefiSuggere.libelle || '',
                        montant: formatMontant(prefiSuggere.resteRembourser),
                    })}
                />
            )}

            <Table
                rowKey="encaissementTrackingId"
                size="small"
                pagination={false}
                dataSource={encaissements}
                columns={columns}
            />

            <div
                style={{
                    marginTop: 12,
                    textAlign: 'right',
                    fontSize: 14,
                }}
            >
                <Text
                    strong
                    style={{ color: isComplete ? '#1a6b3c' : '#dc2626' }}
                >
                    {t('encaissementImputationPicker.totalImpute', { total: formatMontant(totalImpute), cible: formatMontant(montantCible) })}
                    {isComplete ? '  ✓' : ''}
                </Text>
            </div>
        </div>
    );
};

EncaissementImputationPicker.propTypes = {
    sinistreTrackingId: PropTypes.string,
    montantCible: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    prefiSuggere: PropTypes.shape({
        trackingId: PropTypes.string,
        libelle: PropTypes.string,
        resteRembourser: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
    onChange: PropTypes.func,
    disabled: PropTypes.bool,
};

export default EncaissementImputationPicker;
